import React, { useState } from 'react';
import { Users, Calendar, Clock, CheckCircle, AlertCircle, ChevronRight, Archive, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
export function Committees() {
  const [viewMode, setViewMode] = useState<'active' | 'completed'>('active');
  // Updated committees data to include parent-child relationships
  const parentCommittees = [{
    id: 1,
    name: 'Governance Committee',
    description: 'Oversees corporate governance matters and board effectiveness',
    subCommittees: 3,
    members: 15,
    lastMeeting: '2023-08-15',
    status: 'active',
    completion: 70
  }, {
    id: 2,
    name: 'Executive Committee',
    description: 'Handles strategic decision-making and executive oversight',
    subCommittees: 2,
    members: 10,
    lastMeeting: '2023-08-10',
    status: 'active',
    completion: 65
  }, {
    id: 3,
    name: 'Audit Committee',
    description: 'Responsible for financial reporting and compliance oversight',
    subCommittees: 2,
    members: 12,
    lastMeeting: '2023-08-05',
    status: 'active',
    completion: 80
  }];
  const subCommittees = [{
    id: 101,
    parentId: 1,
    name: 'Board Governance Team',
    members: 5,
    lastMeeting: '2023-08-15',
    status: 'active',
    completion: 75
  }, {
    id: 102,
    parentId: 1,
    name: 'Regulatory Compliance Team',
    members: 4,
    lastMeeting: '2023-08-12',
    status: 'active',
    completion: 60
  }, {
    id: 103,
    parentId: 1,
    name: 'Shareholder Relations Team',
    members: 6,
    lastMeeting: '2023-08-08',
    status: 'active',
    completion: 80
  }, {
    id: 201,
    parentId: 2,
    name: 'Strategic Planning Team',
    members: 5,
    lastMeeting: '2023-08-10',
    status: 'active',
    completion: 70
  }, {
    id: 202,
    parentId: 2,
    name: 'Executive Performance Team',
    members: 5,
    lastMeeting: '2023-08-05',
    status: 'active',
    completion: 60
  }, {
    id: 301,
    parentId: 3,
    name: 'Financial Audit Team',
    members: 6,
    lastMeeting: '2023-08-05',
    status: 'active',
    completion: 85
  }, {
    id: 302,
    parentId: 3,
    name: 'Compliance Audit Team',
    members: 6,
    lastMeeting: '2023-07-28',
    status: 'active',
    completion: 75
  }, {
    id: 501,
    parentId: 5,
    name: 'Quality Standards Team',
    members: 5,
    lastMeeting: '2023-07-20',
    status: 'completed',
    completion: 100
  }];
  // Original committees data (for backward compatibility)
  const standardCommittees = [{
    id: 4,
    name: 'Finance Committee',
    members: 8,
    lastMeeting: '2023-08-15',
    status: 'active',
    completion: 75
  }, {
    id: 5,
    name: 'HR Committee',
    members: 6,
    lastMeeting: '2023-08-10',
    status: 'active',
    completion: 60
  }, {
    id: 6,
    name: 'IT Committee',
    members: 5,
    lastMeeting: '2023-08-05',
    status: 'active',
    completion: 80
  }, {
    id: 7,
    name: 'Procurement Committee',
    members: 7,
    lastMeeting: '2023-07-28',
    status: 'active',
    completion: 45
  }, {
    id: 8,
    name: 'Quality Assurance Committee',
    members: 9,
    lastMeeting: '2023-07-20',
    status: 'completed',
    completion: 100
  }];
  // Filter committees based on status
  const filteredParentCommittees = parentCommittees.filter(committee => viewMode === 'active' ? committee.status === 'active' : committee.status === 'completed');
  const filteredStandardCommittees = standardCommittees.filter(committee => viewMode === 'active' ? committee.status === 'active' : committee.status === 'completed');
  return <div data-id="element-582">
      <div className="flex justify-between items-center mb-6" data-id="element-583">
        <h1 className="text-2xl font-bold" data-id="element-584">Committees</h1>
        <div className="flex space-x-3" data-id="element-585">
          <Link to="/committees/formation-decisions-archive" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center" data-id="element-586">
            <FileText size={18} className="mr-2" data-id="element-587" />
            Formation Decisions
          </Link>
          <Link to="/committees/archived" className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center" data-id="element-588">
            <Archive size={18} className="mr-2" data-id="element-589" />
            Archived Committees
          </Link>
          <Link to="/committees/add" className="px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors" data-id="element-590">
            Create New Committee
          </Link>
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8" data-id="element-591">
        <div className="flex items-center p-4 border-b border-gray-200" data-id="element-592">
          <button className={`px-4 py-2 ${viewMode === 'active' ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'} rounded-md mr-4`} onClick={() => setViewMode('active')} data-id="element-593">
            Active
          </button>
          <button className={`px-4 py-2 ${viewMode === 'completed' ? 'bg-orange-500 text-white' : 'bg-gray-200 text-gray-700'} rounded-md`} onClick={() => setViewMode('completed')} data-id="element-594">
            Completed
          </button>
        </div>
        {/* Parent Committees Section */}
        <div className="p-4 border-b border-gray-200" data-id="element-595">
          <h2 className="text-lg font-semibold mb-4" data-id="element-596">
            Hierarchical Committees
          </h2>
          <div className="overflow-x-auto" data-id="element-597">
            <table className="w-full" data-id="element-598">
              <thead data-id="element-599">
                <tr className="bg-gray-50" data-id="element-600">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-601">
                    Committee Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-602">
                    Sub-Committees
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-603">
                    Members
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-604">
                    Last Meeting
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-605">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-606">
                    Completion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-607">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-608">
                {filteredParentCommittees.map(committee => <tr key={committee.id} className="hover:bg-gray-50" data-id="element-609">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-610">
                      <div className="font-medium text-gray-900" data-id="element-611">
                        {committee.name}
                      </div>
                      <div className="text-sm text-gray-500" data-id="element-612">
                        {committee.description}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-613">
                      <div className="flex items-center" data-id="element-614">
                        <Users size={16} className="text-orange-500 mr-2" data-id="element-615" />
                        <span data-id="element-616">{committee.subCommittees}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-617">
                      <div className="flex items-center" data-id="element-618">
                        <Users size={16} className="text-gray-500 mr-2" data-id="element-619" />
                        <span data-id="element-620">{committee.members}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-621">
                      <div className="flex items-center" data-id="element-622">
                        <Calendar size={16} className="text-gray-500 mr-2" data-id="element-623" />
                        <span data-id="element-624">{committee.lastMeeting}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-625">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${committee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-626">
                        {committee.status === 'active' ? 'Active' : 'Completed'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-627">
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-628">
                        <div className={`h-2.5 rounded-full ${committee.completion >= 80 ? 'bg-green-500' : committee.completion >= 50 ? 'bg-orange-500' : 'bg-red-500'}`} style={{
                      width: `${committee.completion}%`
                    }} data-id="element-629"></div>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-630">
                        {committee.completion}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" data-id="element-631">
                      <Link to={`/committees/${committee.id}`} className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-632">
                        View
                      </Link>
                      <Link to={`/committees/${committee.id}/edit`} className="text-orange-500 hover:text-orange-700" data-id="element-633">
                        Edit
                      </Link>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
        {/* Standard Committees Section */}
        <div className="p-4" data-id="element-634">
          <h2 className="text-lg font-semibold mb-4" data-id="element-635">Standard Committees</h2>
          <div className="overflow-x-auto" data-id="element-636">
            <table className="w-full" data-id="element-637">
              <thead data-id="element-638">
                <tr className="bg-gray-50" data-id="element-639">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-640">
                    Committee Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-641">
                    Members
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-642">
                    Last Meeting
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-643">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-644">
                    Completion
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-645">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200" data-id="element-646">
                {filteredStandardCommittees.map(committee => <tr key={committee.id} data-id="element-647">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-648">
                      <div className="font-medium text-gray-900" data-id="element-649">
                        {committee.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-650">
                      <div className="flex items-center" data-id="element-651">
                        <Users size={16} className="text-gray-500 mr-2" data-id="element-652" />
                        <span data-id="element-653">{committee.members}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-654">
                      <div className="flex items-center" data-id="element-655">
                        <Calendar size={16} className="text-gray-500 mr-2" data-id="element-656" />
                        <span data-id="element-657">{committee.lastMeeting}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-658">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${committee.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-659">
                        {committee.status === 'active' ? 'Active' : 'Completed'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-660">
                      <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-661">
                        <div className={`h-2.5 rounded-full ${committee.completion >= 80 ? 'bg-green-500' : committee.completion >= 50 ? 'bg-orange-500' : 'bg-red-500'}`} style={{
                      width: `${committee.completion}%`
                    }} data-id="element-662"></div>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-663">
                        {committee.completion}%
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-664">
                      <Link to={`/committees/${committee.id}`} className="text-orange-500 hover:text-orange-700 mr-3" data-id="element-665">
                        View
                      </Link>
                      <Link to={`/committees/${committee.id}/edit`} className="text-orange-500 hover:text-orange-700" data-id="element-666">
                        Edit
                      </Link>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>;
}